import React, { useEffect, useState } from 'react';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { ThemeProvider, createTheme, Button, SvgIcon, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import InputTheme from '../../Common/Helpers/InputTheme';
import { customValidators, object, string, date } from '../../../Helpers/Validators/customValidators';
import States from '../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import UserIcon from '../../../../icons/user.svg?react';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import { calculateBMI } from '../../../Helpers/Conversion';
import './AboutMe.scss';

export default function AboutMe(props) {
  const formMethods = useForm({
    resolver: yupResolver(AboutMe.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });
  const { handleSubmit } = formMethods;

  const [birthdate, setBirthdate] = useState(props.birthdate);

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    if (field === 'birthdate') {
      setBirthdate(value);
    }
  };

  function calculateTotalHeight() {
    const radix = 10;
    var heightArray = props.formState.height.split(" ")
    var heightFeet = parseInt(heightArray[0], radix);
    var heightInches;
    for (var i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    };
    return heightFeet * 12 + heightInches;
  }

  useEffect(() => {
    if (props.formState.height && props.formState.weight) {
      var weight = props.formState.weight.split(" ")[0];
      var totalHeightInches = calculateTotalHeight();
      handleValueChange("bmi", calculateBMI(totalHeightInches, weight));
    }
  }, [props.formState.height, props.formState.weight])

  const onSubmit = () => {

    const totalHeight = calculateTotalHeight();
    var weight = props.formState.weight.split(" ")[0];

    if (props.validateBMI != null && props.validateBMI(totalHeight, props.formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      var basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.gender = props.formState.gender;
      basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
      basicInfoFromSessionStorage.state = props.formState.state;
      basicInfoFromSessionStorage.height = props.formState.height;
      basicInfoFromSessionStorage.weight = props.formState.weight;
      basicInfoFromSessionStorage.tobacco = props.formState.tobacco;
      basicInfoFromSessionStorage.usResident = props.formState.usResident;
      basicInfoFromSessionStorage.bmi = props.formState.bmi;

      setBasicInfo(basicInfoFromSessionStorage);
      props.onNext();
    }
  }
  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <Container>
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-center" >
              <Col lg={4} className='w-100'>
                <Row className="justify-content-center" >
                  <SvgIcon color='primary' component={UserIcon} className="icons" fontSize="large" />
                </Row>
                <Row className="justify-content-center" >
                  <h3 className="py-3">Profile</h3>
                  <Tooltip
                    title="This information should represent the person who will be insured and covered by the policy, whether that's you or someone else."
                    id="about-tooltip"
                    aria-describedby="about-tooltip"
                    aria-label="more about sort by price"
                    className="toolInfo"
                    role="tooltip"
                    enterTouchDelay={0}
                    leaveTouchDelay={5000}
                    onClose={() => handleTooltipAboutClose()}
                    open={tooltip}
                    slotProps={{
                      tooltip: {
                        className: 'light-tooltip',
                        sx: { fontSize: '14px' }
                      },
                    }}
                  >
                    <button type='button' id="about" role="tooltip" className="quote-tooltip" onMouseEnter={() => handleTooltipAboutOpen()} onClick={() => handleTooltipAboutOpen()} tabIndex="0" aria-disabled="true" aria-describedby="about"><InfoIcon color="action"></InfoIcon></button>
                  </Tooltip>
                </Row>
                <div className="mb-3">
                  <StyledRadioInput
                    name="gender"
                    id="gender"
                    value={props.formState.gender}
                    options={[{ value: "F", label: "Female" }, { value: "M", label: "Male" }]}
                    onChange={e => handleValueChange('gender', e)}
                    isBlankIcon={true}
                  />
                </div>

                <div className="mb-3">
                  <TextInput
                    name="birthdate"
                    id="birthdate"
                    label="Date of Birth"
                    value={birthdate}
                    onChange={(e) => handleValueChange('birthdate', e.target.value)}
                    InputProps={{
                      inputComponent: InputMaskDate,
                    }}
                  />
                </div>

                <div className="mb-3">
                  <SelectInput
                    name="state"
                    id="state"
                    value={props.formState.state}
                    onChange={(e) => handleValueChange('state', e.target.value)}
                    label="State"
                    selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                  />
                </div>

                <Row>
                  <Col>
                    <div className="mb-3">
                      <TextInput
                        name="height"
                        id="height"
                        label="Height"
                        value={props.formState.height}
                        onChange={(e) => handleValueChange('height', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskHeight,
                        }}
                      />
                    </div>
                  </Col>

                  <Col>
                    <div className="mb-3">
                      <TextInput
                        name="weight"
                        id="weight"
                        label="Weight"
                        value={props.formState.weight}
                        onChange={(e) => handleValueChange('weight', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskWeight,
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                <div className="mb-3">
                  <SelectInput
                    name="tobacco"
                    id="tobacco"
                    value={props.formState.tobacco}
                    onChange={(e) => props.onFieldChange('tobacco', e.target.value)}
                    label="Tobacco/Nicotine Use"
                    hideEmptyOption
                    selectOptions={[
                      { value: 'Within the last year', label: 'Within the last year' },
                      { value: 'Within the last 1 to 3 years', label: 'Within the last 1 to 3 years' },
                      { value: 'Within the last 3 to 5 years', label: 'Within the last 3 to 5 years' },
                      { value: 'Over 5 years ago', label: 'Over 5 years ago' },
                      { value: 'Never', label: 'Never' },
                    ]}
                  />
                </div>

              </Col>
            </Row>
            <Row className="mt-4">
              <Col className="text-end pb-4 profile-nav-container">
                <Button variant="contained" size="medium" color="primary" type="submit" className="px-4">
                  NEXT
                </Button>
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </Container>
    </ThemeProvider>
  );
}

AboutMe.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  hideBack: PropTypes.bool,
  ageSpecs: PropTypes.object.isRequired
};

AboutMe.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: customValidators.string()
      .required('Height is required.')
      .isValidHeightString('Invalid height', 3),
    weight: customValidators.string()
      .required('Weight is required.')
      .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    tobacco: string().required('This field is required.'),
  })
};

AboutMe.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    fakeBirthdate: prefill ? '' : (basicInfo && basicInfo.fakeBirthdate) || '',
    tobacco: prefill ? 'Never' : (basicInfo && basicInfo.tobacco) || 'Never',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    usResident: true,
    bmi: (basicInfo && basicInfo.bmi) || undefined,
  };
};
